import { Button } from '@/components/ui/button';
import {
  UploadStatus,
  useVehicleGalleryContext,
} from '@/contexts/vehicle-gallery-provider';
import { generateOptimisticId } from '@/lib/utils';
import { vehicleImageStore } from '@/models/stores/vehicle-images';
import { CircleX, CloudUpload } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { ImageUploadHelper } from '../helpers';
import { PlaceholderImage } from './placeholder';
import { UploadProgressBar } from './progress-bar';
import { VisuallyHiddenInput } from './visually-hidden-input';

export const PreparedFileUploadID = -2;

/**
 * Responsible for:
 *  - Image upload
 *  - Visually communicating about the upload process
 *  - Preparing data that represent "upload preview" image
 */
export const FileUploadBtn = () => {
  const {
    vehicleListingID,
    uploadStatus,
    setUploadStatus,
    vehicleImages,
    setVehicleImages,
    showImageCategoryEditor,
  } = useVehicleGalleryContext();
  const fileUploaderElRef = useRef<HTMLInputElement>(null);

  const onFileSelectCb = useCallback(async () => {
    if (!fileUploaderElRef.current || !fileUploaderElRef.current.files) {
      return;
    }

    // We need to read selected images in base64 encoded format so we can display it to user in form of preview.
    setUploadStatus(UploadStatus.PREPARE);
    const _vehicleImages = [...vehicleImages];
    for (const file of fileUploaderElRef.current.files) {
      let fileCounter = 0;
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      await new Promise<void>((resolve) => {
        fileReader.onload = (event) => {
          fileCounter++;
          if (!event.target || !event.target.result) {
            return;
          }

          _vehicleImages.push({
            ...PlaceholderImage,
            id: generateOptimisticId(),
            image_url: event.target.result.toString(),
            order_index: _vehicleImages.length + fileCounter,
            type: 'upload_prepare',
            file,
          });

          resolve();
        };
      });
    }

    setVehicleImages(_vehicleImages);
  }, [vehicleImages]);

  const onFileUploadCb = async () => {
    try {
      setUploadStatus(UploadStatus.UPLOADING);
      let updatedImages = await ImageUploadHelper.UploadVehicleImages(
        vehicleListingID,
        vehicleImages,
        (_updatedImages) => setVehicleImages([..._updatedImages]),
      );

      setUploadStatus(UploadStatus.PROCESSING);
      const processedImages =
        await ImageUploadHelper.WatchIfUploadedImagesAreProcessed(
          updatedImages,
        );
      setVehicleImages(processedImages);

      // If this is first upload mark the first vehicle image as featured
      if (vehicleImages.every((img) => img.type === 'upload_prepare')) {
        setUploadStatus(UploadStatus.MARKING_FEATURED);
        const firstImageObjectKey = updatedImages[0].objectKey;
        const firstImage = processedImages.find(
          (img) => img.objectKey == firstImageObjectKey,
        );
        if (!firstImage) {
          return;
        }

        const [_, req] = vehicleImageStore.markVehicleImageAsFeatured(
          vehicleListingID,
          firstImage.id,
        );
        await req;
      }

      setUploadStatus(UploadStatus.RE_ORDERING);
      await ImageUploadHelper.ReOrderAllVehicleImages(processedImages);

      setUploadStatus(UploadStatus.FINALIZING);
      await ImageUploadHelper.ReloadImages(vehicleListingID);
      setVehicleImages(vehicleImageStore.getVehicleImages(vehicleListingID));
    } finally {
      if (fileUploaderElRef.current) {
        fileUploaderElRef.current.value = '';
      }
      setUploadStatus(UploadStatus.NONE);
    }
  };

  const onUploadCancelCb = useCallback(() => {
    setVehicleImages((images) =>
      images.filter((img) => img.type !== 'upload_prepare'),
    );
    setUploadStatus(UploadStatus.NONE);

    // Make sure that the file input component input also resets after the prepared upload is cancelled.
    if (fileUploaderElRef.current) {
      fileUploaderElRef.current.value = '';
    }
  }, [vehicleImages]);

  // This hooks keeps track if user removed all selected images for upload, this way we
  // can update `hasImagePendingUpload` flag to `false` once there's no any.
  useEffect(() => {
    if (uploadStatus === UploadStatus.PREPARE) {
      const hasImagePendingUpload = vehicleImages.some(
        (img) => img.type === 'upload_prepare',
      );
      if (!hasImagePendingUpload) {
        setUploadStatus(UploadStatus.NONE);

        // Make sure that the file input component input also resets after the prepared upload is cancelled.
        if (fileUploaderElRef.current) {
          fileUploaderElRef.current.value = '';
        }
      }
    }
  }, [vehicleImages]);

  if (UploadStatus.isProcessingUpload(uploadStatus)) {
    return <UploadProgressBar />;
  }

  if (showImageCategoryEditor) {
    return null;
  }

  return (
    <div className="actions flex flex-row gap-2">
      <Button
        className="cursor-pointer"
        onClick={() => {
          if (uploadStatus === UploadStatus.PREPARE) {
            onFileUploadCb();
            return;
          }

          fileUploaderElRef.current?.click();
        }}
      >
        <CloudUpload />
        <span>
          {uploadStatus === UploadStatus.PREPARE
            ? 'Upload Files'
            : 'Choose files'}
        </span>
        <VisuallyHiddenInput
          ref={fileUploaderElRef}
          type="file"
          accept="image/png, image/jpg, image/jpeg"
          onChange={onFileSelectCb}
          multiple
        />
      </Button>

      {uploadStatus === UploadStatus.PREPARE && (
        <Button
          className="cursor-pointer flex gap-2 border-red-600 text-red-600 hover:text-red-600 hover:bg-red-100"
          variant="outline"
          onClick={onUploadCancelCb}
        >
          <CircleX />
          <span>Cancel</span>
        </Button>
      )}
    </div>
  );
};
